/**
 * WAS DER TIEFBAU DRAUSSEN VORGEFUNDEN HAT - die reinen Regeln.
 *
 * ORIGINALTEXT MARVIN (08.09.2026)
 *   „der Tiefbau hat immer noch nicht die Möglichkeit beim Fertigmelden die
 *    Rohrlänge, Rohrbreite, Rohrverband und Röhrchen anzugeben. Außerdem
 *    fehlen für den Verband und für die Röhrchenfarbe die Dropdown-Menüs.
 *    Die gab es im alten System, die wollen wir wieder haben."
 *
 * DIESE DATEI TRÄGT KEIN JSX UND KEIN NETZ, und das ist der Grund, warum es
 * sie gibt: eine Datei mit JSX lässt sich mit `--experimental-strip-types`
 * nicht laden, und dann bliebe nur die Textsuche - **eine Textsuche misst,
 * dass etwas dasteht, nicht, dass es stimmt.** Dreizehnte Datei dieser Art
 * nach `namen.ts`, `terminraster.ts`, `klebeband.ts`, `kartenmass.ts`,
 * `oberflaeche.ts`, `kartenebene.ts`, `grenzpunkte.ts`, `handbuchmarken.ts`,
 * `tabellenbeschriftung.ts`, `zahlwoerter.ts`, `bruchstellen.ts`,
 * `anlasstext.ts` und `auftragsuche.ts`.
 *
 * Die Felder stehen an ZWEI Stellen auf dem Bildschirm - im Fenster „Von der
 * Baustelle zurückmelden" und beim „Fertig melden" - und die Rechnung
 * dahinter steht EINMAL. Zwei Abschriften laufen beim ersten Nachtrag
 * auseinander, und dann meldet dasselbe Formular je nach Weg etwas anderes.
 */

/** Was in den vier Feldern steht. Immer Text: es sind Eingabefelder. */
export interface TechnikEingabe {
  laenge: string;
  verband: string;
  roehrchen: string;
  breite: string;
}

/** Was der Server zurückgibt, soweit es hier gebraucht wird. */
export interface TechnikStand {
  snr_laenge_ist_m?: unknown;
  snr_rohrverband_ist?: unknown;
  snr_roehrchen_ist?: unknown;
  snr_roehrchenbreite_ist?: unknown;
}

/** Ein Eintrag einer Auswahlliste. Wie `Auswahl1` in `api.ts`. */
export interface Wahl { wert: string; text: string }

/**
 * Was schon gemeldet ist, in die Felder.
 *
 * VORBELEGT WIRD DER ISTWERT UND NIE DER PLANWERT. Wer den Planwert in das
 * Feld schriebe, bekäme beim Speichern eine „Rückmeldung", die nur die
 * Planung wiederholt - und danach ist nicht mehr zu unterscheiden, ob jemand
 * draussen nachgesehen oder nur auf Speichern gedrückt hat.
 */
export function technikAusStand(s: TechnikStand | null): TechnikEingabe {
  const t = (w: unknown) =>
    w === null || w === undefined ? '' : String(w);
  return {
    laenge: t(s?.snr_laenge_ist_m),
    verband: t(s?.snr_rohrverband_ist),
    roehrchen: t(s?.snr_roehrchen_ist),
    breite: t(s?.snr_roehrchenbreite_ist),
  };
}

/**
 * Eine Länge als Zahl - oder null.
 *
 * EINE LEERE ANGABE IST KEINE NULL. `Number('')` ist 0, und null Meter Pipe
 * ist eine Messung, die niemand gemacht hat; derselbe Fehler stand am
 * 04.09.2026 in `kachelbild.ts` und am 08.09.2026 in `alsMenge`. Ein Komma
 * ist ein Dezimalkomma: so schreibt es ein Mensch in diesem Haus.
 */
export function laengeAus(w: string): number | null {
  const roh = String(w ?? '').trim().replace(',', '.');
  if (roh === '') return null;
  const z = Number(roh);
  return Number.isFinite(z) ? z : null;
}

/** Der Rumpf für `POST /auftraege/:id/technikmeldung`. */
export function technikRumpf(e: TechnikEingabe) {
  return {
    snr_laenge_ist_m: laengeAus(e.laenge),
    snr_rohrverband_ist: e.verband.trim(),
    snr_roehrchen_ist: e.roehrchen.trim(),
    snr_roehrchenbreite_ist: e.breite.trim(),
  };
}

/**
 * Hat sich gegenüber dem geladenen Stand etwas geändert?
 *
 * **SIE ENTSCHEIDET, OB BEIM FERTIGMELDEN ÜBERHAUPT GESCHRIEBEN WIRD.** Ohne
 * sie ginge bei jedem „Fertig melden" eine Rückmeldung hinaus, auch wenn
 * niemand ein Feld angefasst hat - und in der Auftragsinfo stünde eine Zeile
 * „Von der Baustelle zurückgemeldet", die nichts meldet.
 *
 * DIE LÄNGE WIRD ALS ZAHL VERGLICHEN UND NICHT ALS TEXT. Die Spalte ist
 * `numeric(10,2)`, der Treiber gibt „42.50" zurück, und im Feld steht „42,5":
 * ein Textvergleich meldete bei jedem Öffnen eine Änderung, die es nicht gibt.
 */
export function technikGeaendert(
  stand: TechnikStand | null, e: TechnikEingabe
): boolean {
  const neu = technikRumpf(e);
  const altLaenge = stand?.snr_laenge_ist_m;
  const alt = altLaenge === null || altLaenge === undefined
    || String(altLaenge).trim() === '' ? null : Number(altLaenge);
  if ((alt === null) !== (neu.snr_laenge_ist_m === null)) return true;
  if (alt !== null && neu.snr_laenge_ist_m !== null
      && alt !== neu.snr_laenge_ist_m) return true;

  const gleich = (a: unknown, b: string) =>
    String(a ?? '').trim() === b;
  if (!gleich(stand?.snr_rohrverband_ist, neu.snr_rohrverband_ist)) return true;
  if (!gleich(stand?.snr_roehrchen_ist, neu.snr_roehrchen_ist)) return true;
  if (!gleich(stand?.snr_roehrchenbreite_ist, neu.snr_roehrchenbreite_ist)) {
    return true;
  }
  return false;
}

/**
 * DIE LISTE FÜHRT DIE HAND, ABER SIE SPERRT NICHT.
 *
 * ORIGINALTEXT MARVIN (08.09.2026)
 *   „Außerdem fehlen für den Verband und für die Röhrchenfarbe die
 *    Dropdown-Menüs. Die gab es im alten System, die wollen wir wieder
 *    haben."
 *
 * **DAS NIMMT DIE ENTSCHEIDUNG VOM 08.09.2026 ZURÜCK, und das ist sein gutes
 * Recht.** In `Rueckmeldung.tsx` stand ein freies Feld, mit der Begründung,
 * eine Rückmeldung von der Baustelle dürfe nicht an einer Schreibweise
 * scheitern. Der GRUND bleibt richtig und wird hier eingelöst, ohne die Liste
 * aufzugeben: **ein Wert, der schon dasteht und in keiner Liste steht, wird
 * der Auswahl HINZUGEFÜGT statt verworfen.**
 *
 * Er entsteht wirklich: der Bauakte-Scan schreibt diese Spalten automatisch,
 * und was in KEINER Liste steht, bleibt dort wörtlich stehen (Regel des
 * Hauses seit dem 23.08.2026). Ohne diese Zeile verlöre ein Dropdown ihn beim
 * ersten Öffnen - lautlos, denn ein `<select>` ohne passende Option zeigt die
 * erste an.
 */
export function mitFremdemWert(optionen: Wahl[], wert: string): Wahl[] {
  const w = String(wert ?? '').trim();
  if (w === '') return optionen;
  if (optionen.some((o) => o.wert === w)) return optionen;
  return [...optionen, { wert: w, text: `${w} (nicht in der Liste)` }];
}
